/**
 * Client-side Squares room for `/leaguesdemo`.
 * League.plays is the source of truth — there is no `/leagues/:id/squares-plays` for proto ids.
 */

import type { League, LeaguePlay, LeagueSettlement } from "./types";
import { leagueSquaresPlayStakeFloor } from "./leagueSquaresStake";

type RoomPlayRow = {
    id: string;
    user_id: number;
    name: string;
    isBot: false;
    selections: Record<string, { run?: string; chips?: number; odds?: string | number }>;
    created_at: string | null;
    stake: number;
    stake_floor?: number | null;
    ball_results: string[] | null;
    rank: null;
    score: null;
    winnings: number | null;
    settled: boolean;
    outcome: string | null;
    return: number | null;
    totalPot: null;
    over_number: number;
};

export type LeagueSquaresRoomPayload = {
    plays: unknown[];
    overs: Record<string, unknown>;
    overOddsLocked: Record<string, boolean>;
    over_economy: Record<string, unknown>;
    myOverNumbers: number[];
    top_prize: number;
    min_players?: unknown;
    pool_entrant_count?: number;
    ball_results?: string[];
};

function parseInnings(raw: string | null): number {
    const n = raw != null ? Math.floor(Number(raw)) : NaN;
    return Number.isFinite(n) && n >= 1 ? n : 1;
}

function parseOverFilter(raw: string | null): number | null {
    if (raw == null || String(raw).trim() === "") return null;
    const n = Math.floor(Number(raw));
    return Number.isFinite(n) && n >= 1 ? n : null;
}

function squaresSourceMatchesRoom(
    source: { game?: string; matchId?: string; marketDetails?: LeaguePlay["marketDetails"] },
    matchId: string,
    inningsNumber: number,
    overFilter: number | null,
): number | null {
    if (source.game !== "squares") return null;
    const over = Math.floor(Number(source.marketDetails?.overNumber));
    if (!Number.isFinite(over) || over < 1) return null;
    const inns = Math.floor(Number(source.marketDetails?.inningsNumber ?? 1)) || 1;
    if (inns !== inningsNumber) return null;
    if (overFilter != null && over !== overFilter) return null;
    const playMatch = String(source.matchId || "").trim();
    if (playMatch && playMatch !== String(matchId)) return null;
    return over;
}

function mapProtoPlayRow(
    source: LeaguePlay | LeagueSettlement,
    over: number,
    settled: boolean,
): RoomPlayRow {
    const selections =
        source.marketDetails?.selections && typeof source.marketDetails.selections === "object"
            ? source.marketDetails.selections
            : {};
    const balls = source.marketDetails?.ballResults;
    const winnings =
        settled && source.delta != null && Number.isFinite(Number(source.delta))
            ? Math.max(0, Number(source.stake || 0) + Number(source.delta))
            : null;
    const placedAt = "placedAt" in source ? source.placedAt : "at" in source ? source.at : null;
    return {
        id: String(source.id),
        user_id: Number(source.userId),
        name: String(source.playerName || "You"),
        isBot: false,
        selections,
        created_at: placedAt ?? null,
        stake: Number(source.stake) || 0,
        stake_floor: leagueSquaresPlayStakeFloor(source) || null,
        ball_results: Array.isArray(balls) ? balls.map((b) => String(b ?? "")) : null,
        rank: null,
        score: null,
        winnings,
        settled,
        outcome: source.outcome ?? null,
        return: winnings,
        totalPot: null,
        over_number: over,
    };
}

export function buildPrototypeLeagueSquaresRoom(
    league: League,
    searchParams: URLSearchParams | Record<string, string>,
    viewerUserId: number = 1,
): LeagueSquaresRoomPayload {
    const q = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);
    const matchId = q.get("matchId") ?? q.get("match_id") ?? "";
    const inningsNumber = parseInnings(q.get("inningsNumber") ?? q.get("innings_number"));
    const overFilter = parseOverFilter(q.get("overNumber") ?? q.get("over_number"));

    const byKey = new Map<string, RoomPlayRow>();
    const absorb = (row: RoomPlayRow) => {
        const key = `${row.user_id}:${row.over_number}`;
        const existing = byKey.get(key);
        if (!existing || (row.settled && !existing.settled)) byKey.set(key, row);
    };

    for (const play of league.plays || []) {
        const over = squaresSourceMatchesRoom(play, matchId, inningsNumber, overFilter);
        if (over == null) continue;
        absorb(mapProtoPlayRow(play, over, play.status === "settled"));
    }
    for (const entry of league.settlements || []) {
        const over = squaresSourceMatchesRoom(entry, matchId, inningsNumber, overFilter);
        if (over == null) continue;
        absorb(mapProtoPlayRow(entry, over, true));
    }

    const plays = [...byKey.values()].sort((a, b) => a.over_number - b.over_number);
    const overs: Record<string, unknown> = {};
    const over_economy: Record<string, unknown> = {};
    const counts = new Map<number, number>();
    for (const p of plays) {
        counts.set(p.over_number, (counts.get(p.over_number) ?? 0) + 1);
    }
    for (const [on, count] of counts) {
        overs[on] = { winners: [], ball_results: null, total_pot: null };
        over_economy[on] = { pool_entrant_count: count, top_prize: 0 };
    }

    const myOverNumbers = [
        ...new Set(
            plays
                .filter((p) => Number(p.user_id) === Number(viewerUserId))
                .map((p) => p.over_number),
        ),
    ].sort((a, b) => a - b);

    return {
        plays,
        overs,
        overOddsLocked: {},
        over_economy,
        myOverNumbers,
        top_prize: 0,
        pool_entrant_count: overFilter != null ? (counts.get(overFilter) ?? 0) : plays.length,
    };
}
